import Button from '@cobliteam/colib-ui/Button';
import { Location } from '@cobliteam/common-types';
import { DatadogTrack } from '@cobliteam/datadog-rum';
import useChatWidget from '@cobliteam/hooks/src/useChatWidget';
import { useTranslation } from '@cobliteam/i18n';
import Typography from '@cobliteam/ui/components/Typography';
import Icon from '@cobliteam/ui/icons';
import { useEffect, useId, useMemo, useState } from 'react';
import type { FallbackProps } from 'react-error-boundary';
import { Link } from 'react-router-dom';
import { useDashboard } from '../../providers/DashboardProvider';

type ErrorPageProps = {
  path?: string;
} & Pick<FallbackProps, 'error'>;

const ErrorPage = ({ error, path }: ErrorPageProps) => {
  const { t } = useTranslation('common');
  const { openIntercom, sendIntercomMessage } = useChatWidget();
  const [isTechnicalDetailsOpen, setIsTechnicalDetailsOpen] = useState(false);
  const [isTechnicalDetailsCopied, setIsTechnicalDetailsCopied] = useState(false);
  const technicalDetailsId = useId();
  const {
    environment: { isDev },
  } = useDashboard();

  const errorTrace = error.stack || t('pages.error.trace_unavailable');

  const technicalDetails = useMemo(
    () =>
      [
        `${t('pages.error.name_label')}: ${error.name || 'Error'}`,
        `${t('pages.error.message_label')}: ${error.message}`,
        path ? `${t('pages.error.route_label')}: ${path}` : undefined,
        `${t('pages.error.trace_label')}:`,
        errorTrace,
      ]
        .filter(Boolean)
        .join('\n'),
    [error.name, error.message, errorTrace, path, t]
  );

  const bugReportMessage = useMemo(
    () =>
      [
        t('pages.error.report_bug_message_intro'),
        `${t('pages.error.route_label')}: ${path ?? window.location.pathname}`,
        `${t('pages.error.message_label')}: ${error.message}`,
        `${t('pages.error.name_label')}: ${error.name || 'Error'}`,
      ].join('\n'),
    [error.message, error.name, path, t]
  );

  const handleTechnicalDetailsToggle = () => {
    setIsTechnicalDetailsOpen((currentValue) => !currentValue);
  };

  const handleCopyTechnicalDetails = async () => {
    if (!navigator.clipboard?.writeText) return;

    await navigator.clipboard.writeText(technicalDetails);
    setIsTechnicalDetailsCopied(true);
  };

  const handleReportBug = () => {
    openIntercom();
    window.setTimeout(() => {
      sendIntercomMessage(bugReportMessage);
    }, 300);
  };

  useEffect(() => {
    const enhancedError = new Error(`ErrorBoundary error on path "${path ?? 'unknown'}": ${error?.message ?? 'No message'}`);
    enhancedError.stack = error?.stack;

    DatadogTrack.error(enhancedError, {
      component: 'ErrorPage',
      path,
      route: path,
      errorMessage: error?.message,
      errorStack: error?.stack,
      errorName: error?.name,
      timestamp: new Date().toISOString(),
    });
  }, [path, error]);

  return (
    <div className='flex h-full min-h-[560px] items-center justify-center bg-gray-1 px-6 py-10 text-center'>
      <main className='grid w-full max-w-4xl gap-6 rounded-lg border border-gray-5 bg-gray-0 p-6 text-left shadow-sm md:grid-cols-[1fr_260px] md:p-8'>
        <section className='flex flex-col gap-6' aria-live='polite'>
          <div className='flex flex-col gap-5 sm:flex-row sm:items-start'>
            <div className='flex size-16 shrink-0 items-center justify-center rounded-lg border border-danger-5 bg-danger-2 text-danger-11'>
              <Icon name='triangle-alert' size='xl' />
            </div>

            <div className='flex max-w-2xl flex-col gap-3'>
              <div className='flex w-fit items-center gap-2 rounded-md border border-warning-5 bg-warning-2 px-3 py-1 text-warning-11'>
                <Icon name='wifi-off' size='sm' />
                <Typography variant='caption-xs-semibold' className='uppercase'>
                  {t('pages.error.eyebrow')}
                </Typography>
              </div>

              <Typography variant='heading-2xl' className='text-gray-12' as='h1'>
                {t('pages.error.title')}
              </Typography>

              <Typography variant='text-md' color='secondary' className='max-w-[64ch]'>
                {t('pages.error.description')}
              </Typography>
            </div>
          </div>

          <div className='flex flex-col justify-center gap-3 sm:flex-row'>
            <Button onClick={() => window.location.reload()}>
              <Icon name='rotate-cw' />
              {t('pages.error.primary_action')}
            </Button>

            <Link to={Location.HOME}>
              <Button variant='outline'>
                <Icon name='house' />
                {t('pages.error.secondary_action')}
              </Button>
            </Link>
          </div>

          {isDev && (
            <div className='rounded-lg border border-gray-5 bg-gray-1 p-4'>
              <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
                <button
                  type='button'
                  className='flex items-center gap-2 text-left font-medium text-gray-12 text-sm'
                  aria-expanded={isTechnicalDetailsOpen}
                  aria-controls={technicalDetailsId}
                  onClick={handleTechnicalDetailsToggle}
                >
                  <Icon name={isTechnicalDetailsOpen ? 'chevron-up' : 'chevron-down'} size='sm' />
                  {t('pages.error.technical_details')}
                </button>

                <Button variant='outline' size='sm' onClick={handleCopyTechnicalDetails}>
                  <Icon name={isTechnicalDetailsCopied ? 'check' : 'copy'} />
                  {isTechnicalDetailsCopied ? t('pages.error.copied_details') : t('pages.error.copy_details')}
                </Button>
              </div>

              {isTechnicalDetailsOpen && (
                <div id={technicalDetailsId} className='mt-3 space-y-3 rounded-md bg-gray-0 p-3 font-mono text-gray-11 text-sm'>
                  <div>
                    <span className='font-medium font-sans text-gray-12'>{t('pages.error.name_label')}: </span>
                    {error.name || 'Error'}
                  </div>

                  <div>
                    <span className='font-medium font-sans text-gray-12'>{t('pages.error.message_label')}: </span>
                    {error.message}
                  </div>

                  {path && (
                    <div>
                      <span className='font-medium font-sans text-gray-12'>{t('pages.error.route_label')}: </span>
                      {path}
                    </div>
                  )}

                  <div className='space-y-2'>
                    <span className='font-medium font-sans text-gray-12'>{t('pages.error.trace_label')}:</span>
                    <pre className='max-h-72 overflow-auto whitespace-pre-wrap rounded-md bg-gray-2 p-3 text-gray-11'>{errorTrace}</pre>
                  </div>
                </div>
              )}
            </div>
          )}
        </section>

        <aside className='flex flex-col justify-between gap-6 rounded-lg border border-primary-4 bg-primary-2 p-5'>
          <div className='flex flex-col gap-4'>
            <div className='flex items-start gap-3'>
              <div className='flex size-10 shrink-0 items-center justify-center rounded-md bg-primary-3 text-primary-11'>
                <Icon name='shield-check' size='md' />
              </div>

              <div className='flex flex-col gap-1'>
                <Typography variant='text-md-semibold' className='text-gray-12'>
                  {t('pages.error.status_title')}
                </Typography>

                <Typography variant='text-sm' color='secondary'>
                  {t('pages.error.status_description')}
                </Typography>
              </div>
            </div>

            <div className='rounded-md border border-primary-5 bg-gray-0 p-3'>
              <Typography variant='text-sm-medium' className='text-gray-12'>
                {t('pages.error.report_bug_title')}
              </Typography>

              <Typography variant='text-sm' color='secondary' className='mt-1'>
                {t('pages.error.report_bug_description')}
              </Typography>
            </div>
          </div>

          <Button variant='outline' onClick={handleReportBug}>
            <Icon name='message-circle-warning' />
            {t('pages.error.report_bug_action')}
          </Button>
        </aside>
      </main>
    </div>
  );
};

export default ErrorPage;
